import ApiService from '../ApiService';
import RootApiService from '../RootApiService';
import { translate } from '@/i18n';

import { makeLogger } from '@/services/utils/logger';
import { LOGGING, VITE_BASE_URL } from '@/config';

const DEBUG_LOG = false;
const log = makeLogger('ClubService', DEBUG_LOG, LOGGING.NET_SERVICES_LOG);

/**
 * Сервис для работы с chess-clubs через API.
 * Пока без модели: возвращаем plain-объекты.
 */
class ClubApiService extends ApiService {
  async getHome(clubId) {
    return (await RootApiService.api.get(`/public/clubs/${clubId}/home`, { skipToken: true })).data.data;
  }
  async deleteClubImage(clubId, imageId) {
    await RootApiService.api.delete(`/chess-clubs/${clubId}/settings/images/${imageId}`);
  }
  async getSettings(clubId) {
    const response = await RootApiService.api.get(`/chess-clubs/${clubId}/settings`);
    return this.deserialize(response.data.data);
  }
  async saveSettings(clubId, data) {
    const response = await RootApiService.api.put(`/chess-clubs/${clubId}/settings`, { data });
    return this.deserialize(response.data.data);
  }
  async uploadSettingsImage(clubId, file, kind, title) {
    const data = new FormData();
    data.append('image', file);
    data.append('kind', kind);
    if (title?.trim()) data.append('title', title.trim());
    const response = await RootApiService.api.post(`/chess-clubs/${clubId}/settings/images`, data);
    return response.data.data;
  }
  async searchCities(clubId, input, language) {
    const response = await RootApiService.api.get(`/chess-clubs/${clubId}/settings/cities`, { params: { input, language } });
    return response.data.data;
  }
  async getCity(clubId, placeId, language) {
    const response = await RootApiService.api.get(`/chess-clubs/${clubId}/settings/city`, { params: { placeId, language } });
    return response.data.data;
  }
  async getPlayerMarkOptions(clubId) {
    const load = async (method) => {
      const rows = [];
      let page = 1;
      let pageCount = 1;
      do {
        const result = await this[method](clubId, { page, pageSize: 100 });
        rows.push(...result.data);
        pageCount = result.meta?.pagination?.pageCount || 1;
        page++;
      } while (page <= pageCount);
      return rows;
    };
    const [labels, categories] = await Promise.all([load('listLabels'), load('listCategories')]);
    return { labels, categories };
  }
  async isTitleCodeAvailable(code, titleId = null) {
    const filters = { code: { $eqi: code.trim() } };
    if (titleId) filters.documentId = { $ne: titleId };
    const response = await RootApiService.api.get('/titles', { params: { filters, fields: ['documentId'], pagination: { page: 1, pageSize: 1 } } });
    return response.data.data.length === 0;
  }
  async listTitles(clubId, { page = 1, pageSize = 25, search = '' } = {}) {
    if (!clubId) throw new Error('clubId is required');
    const filters = { $and: [{ $or: [
      { scope: { $eq: 'fide' } },
      { scope: { $eq: 'local' }, chess_club: { documentId: { $eq: clubId } } },
    ] }] };
    if (search.trim()) filters.$and.push({ $or: ['name', 'code'].map(key => ({ [key]: { $containsi: search.trim() } })) });
    const response = await RootApiService.api.get('/titles', { params: { filters, pagination: { page, pageSize }, sort: ['scope:desc', 'priority:asc', 'name:asc', 'documentId:asc'] } });
    return { data: (response.data?.data || []).map(entry => ({ ...entry, id: entry.documentId })), meta: response.data?.meta || {} };
  }
  createLocalTitle(clubId, data) { return this.saveLocalTitle(null, data, clubId); }
  updateLocalTitle(id, data) {
    if (!id) throw new Error('titleId is required');
    return this.saveLocalTitle(id, data);
  }
  async saveLocalTitle(id, values = {}, clubId) {
    if (!id && !clubId) throw new Error('clubId is required');
    const data = Object.fromEntries(['name', 'code', 'note', 'priority', 'isActive']
      .filter(key => values[key] !== undefined).map(key => [key, values[key]]));
    if (!id) Object.assign(data, { scope: 'local', chess_club: clubId });
    const response = id ? await RootApiService.api.put(`/titles/${id}`, { data }) : await RootApiService.api.post('/titles', { data });
    const entry = response.data?.data;
    return entry ? { ...entry, id: entry.documentId } : null;
  }
  async deleteLocalTitle(id) {
    if (!id) throw new Error('titleId is required');
    await RootApiService.api.delete(`/titles/${id}`);
  }
  listLabels(clubId, options) { return this.listMarks('labels', clubId, options); }
  listCategories(clubId, options) { return this.listMarks('categories', clubId, options); }
  createLabel(clubId, data) { return this.saveMark('labels', null, data, clubId); }
  createCategory(clubId, data) { return this.saveMark('categories', null, data, clubId); }
  updateLabel(id, data) { if (!id) throw new Error('labelId is required'); return this.saveMark('labels', id, data); }
  updateCategory(id, data) { if (!id) throw new Error('categoryId is required'); return this.saveMark('categories', id, data); }
  uploadLabelImage(id, file) { return this.uploadMarkImage('labels', id, file); }
  uploadCategoryImage(id, file) { return this.uploadMarkImage('categories', id, file); }
  selectLabelImage(id, mediaId) { return this.selectMarkImage('labels', id, mediaId); }
  selectCategoryImage(id, mediaId) { return this.selectMarkImage('categories', id, mediaId); }
  deleteLabelImage(id) { return this.deleteMarkImage('labels', id); }
  deleteCategoryImage(id) { return this.deleteMarkImage('categories', id); }
  async deleteLabel(id) {
    if (!id) throw new Error('labelId is required');
    await RootApiService.api.delete(`/club-labels/${id}`);
  }
  async deleteCategory(id) {
    if (!id) throw new Error('categoryId is required');
    await RootApiService.api.delete(`/club-categories/${id}`);
  }

  // Списки возвращают { data, meta }; пагинация выполняется сервером.
  async listMarks(resource, clubId, { page = 1, pageSize = 25, search = '', sort = ['sortOrder:asc', 'name:asc'] } = {}) {
    if (!clubId) throw new Error('clubId is required');
    const filters = { club: { documentId: { $eq: clubId } } };
    if (search.trim()) filters.$or = ['name', 'shortName'].map(key => ({ [key]: { $containsi: search.trim() } }));
    const response = await RootApiService.api.get(`/club-${resource}`, {
      params: { filters, sort, pagination: { page, pageSize }, populate: { image: true } },
    });
    return { data: (response.data?.data || []).map(entry => ({ ...entry, id: entry.documentId })), meta: response.data?.meta || {} };
  }

  async saveMark(resource, id, values = {}, clubId) {
    if (!id && !clubId) throw new Error('clubId is required');
    const data = Object.fromEntries(['name', 'shortName', 'description', 'color', 'visibility', 'sortOrder']
      .filter(key => values[key] !== undefined).map(key => [key, values[key]]));
    if (!id) data.club = clubId;
    const response = id
      ? await RootApiService.api.put(`/club-${resource}/${id}`, { data })
      : await RootApiService.api.post(`/club-${resource}`, { data });
    const entry = response.data?.data;
    return entry ? { ...entry, id: entry.documentId } : null;
  }

  async uploadMarkImage(resource, id, file) {
    if (!id) throw new Error('markId is required');
    if (!file) throw new Error('image file is required');
    const formData = new FormData();
    formData.append('image', file);
    const response = await RootApiService.api.post(`/club-${resource}/${id}/image`, formData);
    const entry = response.data?.data;
    return entry ? { ...entry, id: entry.documentId } : null;
  }

  async listClubImages({ page = 1, pageSize = 50 } = {}) {
    const response = await RootApiService.api.get('/club-images', {
      params: { pagination: { page, pageSize } },
    });
    return {
      data: response.data?.data || [],
      meta: response.data?.meta || {},
    };
  }

  async getAllClubImages() {
    const images = [];
    let page = 1;
    let pageCount = 1;
    do {
      const result = await this.listClubImages({ page, pageSize: 100 });
      images.push(...result.data);
      pageCount = result.meta?.pagination?.pageCount || 1;
      page++;
    } while (page <= pageCount);
    return images;
  }

  async selectMarkImage(resource, id, mediaId) {
    if (!id) throw new Error('markId is required');
    const numericMediaId = Number(mediaId);
    if (!Number.isInteger(numericMediaId) || numericMediaId < 1) throw new Error('mediaId is required');
    const response = await RootApiService.api.put(`/club-${resource}/${id}/image`, {
      data: { mediaId: numericMediaId },
    });
    const entry = response.data?.data;
    return entry ? { ...entry, id: entry.documentId } : null;
  }

  async deleteMarkImage(resource, id) {
    if (!id) throw new Error('markId is required');
    const response = await RootApiService.api.delete(`/club-${resource}/${id}/image`);
    const entry = response.data?.data;
    return entry ? { ...entry, id: entry.documentId } : null;
  }

  /**
   * Получить список клубов (легковесно, без связей).
   * @param {Object} [options]
   * @param {number} [options.limit=100] - Максимум записей, который нужно запросить у API.
   * @param {string} [options.searchQuery] - Поиск по названию, slug, городу или стране.
   * @param {string} [options.cityQuery] - Фильтр по городу/стране.
   * @param {{page?:number,pageSize?:number}} [options.pagination] - Пагинация.
   * @param {boolean} [options.returnMeta=false] - Вернуть meta Strapi вместе с data.
   * @returns {Promise<Array<{id:string, slug:string|null, name:string|null, city:string|null, country:string|null, logo:string|null, logoUrl:string|null, avatar:string|null, avatarUrl:string|null, background:string|null, backgroundUrl:string|null, design:any|null, tournamentsCount:number, playersCount:number}>>}
   */
  async getAll(options = {}) {
    const {
      searchQuery = '',
      cityQuery = '',
      cityPlaceId = '',
      countryCode = '',
      language = 'en',
      pagination = null,
      returnMeta = false,
    } = options || {};
    const limit = Number.isFinite(Number(options?.limit))
      ? Math.max(1, Math.floor(Number(options.limit)))
      : 100;
    const paginationParams = pagination
      ? {
          page: pagination.page ?? 1,
          pageSize: pagination.pageSize ?? limit,
        }
      : {
          page: 1,
          pageSize: limit,
        };
    const filters = {};
    const andFilters = [];
    const search = String(searchQuery || '').trim();
    const city = String(cityQuery || '').trim();
    const resolveLocation = async query => {
      if (!query) return [];
      const response = await RootApiService.api.get('/public/location', { params: { q: query, language }, skipToken: true });
      const location = response.data?.data || {};
      return [
        ...(location.cityIds?.length ? [{ cityPlaceId: { $in: location.cityIds } }] : []),
        ...(location.countryCodes?.length ? [{ countryCode: { $in: location.countryCodes } }] : []),
      ];
    };
    const [searchLocation, cityLocation] = await Promise.all([
      resolveLocation(search), cityPlaceId ? Promise.resolve([{ cityPlaceId }]) : resolveLocation(city),
    ]);

    if (search) {
      andFilters.push({
        $or: [
          { name: { $containsi: search } },
          { slug: { $containsi: search } },
          ...searchLocation,
        ],
      });
    }

    if (city || cityPlaceId) {
      andFilters.push(cityLocation.length ? { $or: cityLocation } : { documentId: { $eq: '__no_location__' } });
    }
    if (countryCode) andFilters.push({ countryCode });

    if (andFilters.length === 1) {
      Object.assign(filters, andFilters[0]);
    } else if (andFilters.length > 1) {
      filters.$and = andFilters;
    }

    log('getAll', 'request', { filters, pagination: paginationParams });

    try {
      const res = await this.api.get('/', {
        params: {
          filters,
          fields: ['documentId', 'name', 'description', 'slug', 'city', 'country', 'cityPlaceId', 'countryCode', 'design', 'currency', 'entryFee', 'welcomeCard', 'contacts', 'contactDescription'],
          populate: {
            welcomeImages: true,
            socialPreview: { fields: ['url'] },
            logo: { fields: ['url', 'width', 'height', 'formats'] },
            avatar: { fields: ['url', 'width', 'height', 'formats'] },
            background: { fields: ['url', 'width', 'height', 'formats'] },
            export_standing_regular_bg: { fields: ['url', 'width', 'height', 'formats'] },
            tournaments: { fields: ['documentId'] },
            players: { fields: ['documentId'] },
          },
          sort: ['name:asc'],
          pagination: paginationParams,
        },
        skipToken: true,
      });

      const entries = this.asArray(res?.data?.data);
      log('getAll', 'entries', entries);

      const data = entries
        .map((entry, index) => {
          try {
            return this.deserialize(entry);
          } catch (e) {
            log.error('getAll', `Ошибка при обработке entry[${index}]`, e?.message || e, entry);
            return null;
          }
        })
        .filter(Boolean);

      if (returnMeta) {
        return { data, meta: res?.data?.meta || null };
      }

      return data;
    } catch (e) {
      log.error('getAll', 'Ошибка при получении клубов', e);
      if (options.throwOnError) throw e;
      return returnMeta ? { data: [], meta: null } : [];
    }
  }

  /**
   * Получает клуб по documentId
   * @param {string} id
   * @returns {Promise<Object|null>}
   */
  async getById(id) {
    if (!id) {
      log.error('getById', 'id не указан');
      return null;
    }
    return this.#getBy({ documentId: { $eq: id } });
  }

  /**
   * Получает клуб по slug
   * @param {string} slug
   * @returns {Promise<Object|null>}
   */
  async getBySlug(slug) {
    if (!slug) {
      log.error('getBySlug', 'slug не указан');
      return null;
    }
    return this.#getBy({ slug: { $eq: slug } });
  }

  /**
   * Универсальный метод получения одного клуба по фильтру.
   * Пример: await this.getBy({ documentId: { $eq: id } }) или { slug: { $eq: slug } }
   * @param {Object} filter
   * @returns {Promise<Object|null>}
   */
  async #getBy(filter) {
    try {
      const res = await this.api.get('/', {
        params: {
          filters: {
            ...(filter || {})
          },
          fields: ['name', 'description', 'slug', 'city', 'country', 'cityPlaceId', 'countryCode', 'design', 'currency', 'entryFee', 'welcomeCard', 'contacts', 'contactDescription'],
          populate: {
            welcomeImages: true,
            socialPreview: { fields: ['url'] },
            logo: { fields: ['url', 'width', 'height', 'formats'] },
            avatar: { fields: ['url', 'width', 'height', 'formats'] },
            background: { fields: ['url', 'width', 'height', 'formats'] },
            export_standing_regular_bg: { fields: ['url', 'width', 'height', 'formats'] },
          },
          pagination: { page: 1, pageSize: 1 },
        },
        skipToken: true,
      });

      const rawData = res?.data?.data;
      if (!Array.isArray(rawData)) throw new Error(translate('serviceMessages.invalidClubResponse'));
      const entry = rawData[0];

      if (!entry) {
        log.warn('getBy', 'Клуб по фильтру не найден', filter);
        return null;
      }

      return this.deserialize(entry);
    } catch (e) {
      log.error('getBy', 'Ошибка при получении клуба по фильтру', { filter, error: e });
      throw e;
    }
  }

  /**
   * Десериализация chess-club из Strapi v5.
   */
  deserialize(entry) {
    const data = entry?.attributes || entry;
    const id = entry?.documentId || data?.documentId;

    if (!id || !data) {
      throw new Error(translate('serviceMessages.invalidClubResponse'));
    }

    const name = data?.name ?? null;
    const slug = data?.slug ?? null;
    const city = data?.city ?? null;
    const country = data?.country ?? null;
    const design = (data?.design && typeof data.design === 'object') ? data.design : (data?.design ?? null);

    // Media field variants (Strapi media returns relative url like /uploads/..)
    const logoRel =
      data?.logo?.url ||
      data?.logo?.data?.attributes?.url ||
      data?.logo?.data?.url ||
      null;

    const avatarRel =
      data?.avatar?.url ||
      data?.avatar?.data?.attributes?.url ||
      data?.avatar?.data?.url ||
      null;

    const exportStandingRegularBgRel =
      data?.export_standing_regular_bg?.url ||
      data?.export_standing_regular_bg?.data?.attributes?.url ||
      data?.export_standing_regular_bg?.data?.url ||
      null;

    const backgroundRel =
      data?.background?.url ||
      data?.background?.data?.attributes?.url ||
      data?.background?.data?.url ||
      null;

    const normalizeUrl = (url) => {
      if (!url || typeof url !== 'string') return null;
      const u = url.trim();
      if (!u) return null;
      // Already absolute
      if (/^https?:\/\//i.test(u)) return u;
      // Relative -> prefix with base
      const base = (typeof VITE_BASE_URL === 'string') ? VITE_BASE_URL.trim() : '';
      if (!base) return u;
      // Avoid double slashes
      const b = base.endsWith('/') ? base.slice(0, -1) : base;
      const p = u.startsWith('/') ? u : `/${u}`;
      return `${b}${p}`;
    };

    const logoAbs = normalizeUrl(logoRel);
    const avatarAbs = normalizeUrl(avatarRel);
    const backgroundAbs = normalizeUrl(backgroundRel);
    const exportStandingRegularBgAbs = normalizeUrl(exportStandingRegularBgRel);
    const tournamentsCount = this.asArray(data?.tournaments?.data || data?.tournaments).length;
    const playersCount = this.asArray(data?.players?.data || data?.players).length;

    return {
      id,
      slug,
      name,
      description: data.description || '',
      socialPreviewUrl: normalizeUrl(data.socialPreview?.url),
      contacts: Array.isArray(data.contacts) ? data.contacts : [],
      contactDescription: data.contactDescription || '',
      welcomeCard: data.welcomeCard ? { ...data.welcomeCard, items: (data.welcomeCard.items || []).map(item => ({
        ...item, image: (data.welcomeImages || []).find(image => image.id === item.imageId) || null,
      })) } : null,
      currency: data?.currency || null,
      entryFee: data?.entryFee == null ? null : Number(data.entryFee),
      city,
      country,
      countryCode: data.countryCode || null,
      cityPlaceId: data.cityPlaceId || null,
      images: Object.fromEntries(['avatar', 'logo', 'background', 'export_standing_regular_bg'].map(key => [key, data[key] || null])),
      // keep raw relative (backward compatibility)
      logo: logoRel,
      // preferred field for <img :src>
      logoUrl: logoAbs,
      // keep raw relative (backward compatibility)
      avatar: avatarRel,
      // preferred field for <img :src>
      avatarUrl: avatarAbs,
      background: backgroundRel,
      backgroundUrl: backgroundAbs,
      exportStandingRegularBg: exportStandingRegularBgRel,
      exportStandingRegularBgUrl: exportStandingRegularBgAbs,
      tournamentsCount,
      playersCount,
      design,
    };
  }
}

export default new ClubApiService('chess-clubs');
